import React from 'react';
import { CheckCircle, Cancel, Schedule, Edit, Block, Person, AdminPanelSettings, Phone, Email, CalendarToday } from '@mui/icons-material';

export const MemberCard = ({ member, onViewDetails, onChangeRole, onRevokeAccess, onReactivate }) => {
    const getStatusColor = (status) => {
        const colors = {
            'active': 'bg-green-100 text-green-800 border-green-200',
            'pending': 'bg-amber-100 text-amber-800 border-amber-200',
            'revoked': 'bg-red-100 text-red-800 border-red-200',
            'abandoned': 'bg-gray-100 text-gray-800 border-gray-200'
        };
        return colors[status] || 'bg-gray-100 text-gray-800 border-gray-200';
    };

    const getStatusIcon = (status) => {
        const icons = {
            'active': <CheckCircle className="text-green-500" />,
            'pending': <Schedule className="text-amber-500" />,
            'revoked': <Cancel className="text-red-500" />,
            'abandoned': <Cancel className="text-gray-500" />
        };
        return icons[status] || <Schedule className="text-gray-500" />;
    };

    const getStatusLabel = (status) => {
        const labels = {
            'active': 'Actif',
            'pending': 'En attente',
            'revoked': 'Révoqué',
            'abandoned': 'Abandonné'
        };
        return labels[status] || status;
    };

    const getRoleColor = (role) => {
        return role === 'admin' || role?.toLowerCase() === 'admin'
            ? 'bg-amber-100 text-amber-800 border-amber-200'
            : 'bg-blue-100 text-blue-800 border-blue-200';
    };

    const getRoleLabel = (role) => {
        return role === 'admin' ? 'Administrateur' : 'Caissier(e)';
    };

    const formatDate = (date) => {
        return new Date(date).toLocaleDateString('fr-FR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        });
    };

    const getRoleIcon = (role) => {
        return role === 'admin' || role?.toLowerCase() === 'admin' 
            ? <AdminPanelSettings className="text-amber-600" />
            : <Person className="text-amber-600" />;
    };

    return (
        <div className="bg-white rounded-2xl p-6 border border-gray-200 shadow-sm hover:shadow-md transition-all duration-200 group">
            {/* En-tête */}
            <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-3">
                    <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center">
                        {getRoleIcon(member.role)}
                    </div>
                    <div>
                        <h3 className="font-bold text-gray-900 text-lg group-hover:text-amber-700 transition-colors">
                            {member.name}
                        </h3>
                        <div className="flex items-center space-x-1 text-sm text-gray-600">
                            <Phone className="text-xs" />
                            <span>{member.phone}</span>
                        </div>
                    </div>
                </div>
                <span className={`inline-flex items-center space-x-1 px-3 py-1 rounded-full text-sm font-semibold border ${getStatusColor(member.status)}`}>
                    {getStatusIcon(member.status)}
                    <span>{getStatusLabel(member.status)}</span>
                </span>
            </div>

            {/* Informations */}
            <div className="space-y-3 mb-4">
                <div className="flex items-center space-x-2 text-sm">
                    <Email className="text-gray-400 text-sm" />
                    <span className="text-gray-600 truncate">{member.email}</span>
                </div>
                <div className="flex items-center justify-between">
                    <span className="text-gray-600 text-sm">Rôle:</span>
                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${getRoleColor(member.role)}`}>
                        {getRoleLabel(member.role)}
                    </span>
                </div>
                <div className="flex items-center space-x-2 text-sm">
                    <CalendarToday className="text-gray-400 text-sm" />
                    <span className="text-gray-600">Membre depuis {formatDate(member.joinDate)}</span>
                </div>
            </div>

            {/* Permissions */}
            {member.permissions && member.permissions.length > 0 && (
                <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-600 mb-2">Permissions:</label>
                    <div className="flex flex-wrap gap-1">
                        {member.permissions.slice(0, 2).map((permission, index) => (
                            <span key={index} className="px-2 py-1 bg-amber-50 text-amber-700 rounded-lg text-xs border border-amber-200">
                                {permission}
                            </span>
                        ))}
                        {member.permissions.length > 2 && (
                            <span className="px-2 py-1 bg-amber-50 text-amber-700 rounded-lg text-xs border border-amber-200">
                                +{member.permissions.length - 2} autres
                            </span>
                        )}
                    </div>
                </div>
            )}

            {/* Actions */}
            <div className="flex space-x-2">
                <button
                    onClick={() => onViewDetails(member)}
                    className="flex-1 flex items-center justify-center space-x-2 px-3 py-2.5 bg-amber-50 text-amber-700 rounded-xl hover:bg-amber-100 transition-all duration-300 border border-amber-200 hover:border-amber-300"
                >
                    <Edit className="text-sm" />
                    <span className="text-sm font-semibold">Détails</span>
                </button>

                {member.status === 'active' && (
                    <button
                        onClick={() => onRevokeAccess(member)}
                        className="flex-1 flex items-center justify-center space-x-2 px-3 py-2.5 bg-red-50 text-red-700 rounded-xl hover:bg-red-100 transition-all duration-300 border border-red-200 hover:border-red-300"
                    >
                        <Block className="text-sm" />
                        <span className="text-sm font-semibold">Révoquer</span>
                    </button>
                )}

                {(member.status === 'revoked' || member.status === 'declined') && (
                    <button
                        onClick={() => onReactivate(member)}
                        className="flex-1 flex items-center justify-center space-x-2 px-3 py-2.5 bg-green-50 text-green-700 rounded-xl hover:bg-green-100 transition-all duration-300 border border-green-200 hover:border-green-300"
                    >
                        <CheckCircle className="text-sm" />
                        <span className="text-sm font-semibold">Réactiver</span>
                    </button>
                )}
            </div>
        </div>
    );
};